import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchLeaderboard } from './api';
import { LEADERBOARD_MODELS, SCORE_PRECISION } from './leaderboard-config';
import LLMIcon from './LLMIcon';

/**
 * Interpolate between two hex colors.
 * Returns a hex color at `t` between 0 and 1.
 */
function lerpColor(colorA, colorB, t) {
  const a = [
    parseInt(colorA.slice(1, 3), 16),
    parseInt(colorA.slice(3, 5), 16),
    parseInt(colorA.slice(5, 7), 16),
  ];
  const b = [
    parseInt(colorB.slice(1, 3), 16),
    parseInt(colorB.slice(3, 5), 16),
    parseInt(colorB.slice(5, 7), 16),
  ];
  const r = Math.round(a[0] + (b[0] - a[0]) * t);
  const g = Math.round(a[1] + (b[1] - a[1]) * t);
  const bl = Math.round(a[2] + (b[2] - a[2]) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

/**
 * Generate a gradient palette from light sea blue → purple.
 * @param {number} count - Number of colors to generate.
 * @returns {string[]}
 */
function gradientBarColors(count) {
  // Light sea-blue → indigo → purple
  const start = '#38BDF8';  // sky-400 — shallow sea blue
  const mid = '#818CF8';    // indigo-400
  const end = '#A855F7';    // purple-500
  if (count <= 0) return [];
  if (count === 1) return [end];
  const colors = [];
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1);
    // Ease through mid point
    const color = t < 0.5
      ? lerpColor(start, mid, t * 2)
      : lerpColor(mid, end, (t - 0.5) * 2);
    colors.push(color);
  }
  return colors;
}

/**
 * Custom Y-axis tick that renders model icon + model name.
 */
function CustomYAxisTick({ x, y, payload, index }) {
  const modelName = payload.value;

  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x="-130" y="-14" width="130" height="28" style={{ overflow: 'visible' }}>
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          className="leaderboard-tick"
          style={{ justifyContent: 'flex-end', width: '100%' }}
        >
          <span className="tick-label">
            {modelName}
          </span>
          <LLMIcon modelName={modelName} size={18} />
        </div>
      </foreignObject>
    </g>
  );
}

function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function loadLeaderboard() {
      setIsLoading(true);
      setError('');

      try {
        // Extract just the model names from the config and pass them to the API
        // Score metric is hardcoded on the backend — no longer sent from frontend
        const modelNames = Object.values(LEADERBOARD_MODELS);
        const data = await fetchLeaderboard(modelNames);
        if (!isMounted) return;
        setLeaderboard(data);
      } catch (err) {
        if (!isMounted) return;
        setError(err.message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadLeaderboard();
  }, []);

  if (isLoading) {
    return (
      <div className="leaderboard-panel leaderboard-status" role="status">
        <div className="leaderboard-spinner" aria-hidden="true"></div>
        <p>Loading leaderboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-panel leaderboard-status leaderboard-error" role="alert">
        <h3>Leaderboard unavailable</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (!leaderboard || !Array.isArray(leaderboard.models)) {
    return (
      <div className="leaderboard-panel leaderboard-status leaderboard-error" role="alert">
        <h3>Leaderboard unavailable</h3>
        <p>Leaderboard data is missing or malformed.</p>
      </div>
    );
  }

  const updatedLabel = new Date(leaderboard.updatedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  // Round scores using frontend precision config
  const models = leaderboard.models.map((m) => ({
    ...m,
    score: Number(m.score.toFixed(SCORE_PRECISION)),
  }));

  // Auto-scaled X axis ceiling: max score → next 0.05 step
  const maxScore = Math.max(...models.map((m) => m.score), 0);
  const axisMax = Math.ceil(maxScore / 0.05) * 0.05;

  const colors = gradientBarColors(models.length);

  return (
    <section className="leaderboard-panel" aria-labelledby="leaderboard-title">
      <div className="leaderboard-meta">
        <div>
          <h2 id="leaderboard-title">LLM Humor Leaderboard</h2>
        </div>
        <div className="leaderboard-stats">
          <span>Updated: {updatedLabel}</span>
        </div>
      </div>

      <div className="leaderboard-chart-wrap">
        <ResponsiveContainer width="100%" height={420}>
          <BarChart
            data={models}
            layout="vertical"
            margin={{ top: 12, right: 24, bottom: 12, left: 130 }}
          >
            <CartesianGrid stroke="#d7d2c8" strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" domain={[0, axisMax]} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}`} />
            <YAxis
              type="category"
              dataKey="model"
              tickLine={false}
              axisLine={false}
              width={130}
              tick={<CustomYAxisTick barColors={colors} />}
            />
            <Tooltip
              cursor={{ fill: 'rgba(15, 23, 42, 0.08)' }}
              formatter={(value) => [`${value}`, 'Score']}
            />
            <Bar dataKey="score" radius={[0, 14, 14, 0]}>
              {models.map((entry, index) => (
                <Cell key={entry.model} fill={colors[index]} />
              ))}
              <LabelList dataKey="score" position="right" fill="#1f2937" />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

export default Leaderboard;
